import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { MatchPreview } from '../api/contract'
import { formatDate } from '../lib/time'
import './Tables.css'
import { InfoTip } from './InfoTip'

function FaceIcon() {
  return (
    <div className="face sm" aria-hidden="true">
      <svg width="19" height="19" viewBox="0 0 20 20" fill="currentColor">
        <circle cx="10" cy="6.8" r="3.4" />
        <path d="M3.6 17.5c0-3.5 2.9-6.1 6.4-6.1s6.4 2.6 6.4 6.1z" />
      </svg>
    </div>
  )
}

function MatchRow({ match }: { match: MatchPreview }) {
  return (
    <div className="mn">
      <FaceIcon />
      <div>
        <div className="mn-m">{match.match_score}% Match</div>
        <div className="mn-n">{match.display_name}</div>
        <div className="ex-i">{match.reference_code}</div>
      </div>
    </div>
  )
}

export function SavedSearches() {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['saved-searches'],
    queryFn: () => api.getSavedSearches(),
  })
  const { data: matches } = useQuery({ queryKey: ['top-matches'], queryFn: () => api.getTopMatches() })

  const totalNew = data?.items.reduce((sum, s) => sum + s.new_matches, 0) ?? 0
  const shown = matches?.items.length ?? 0

  return (
    <section className="sec pair">
      <div className="card" style={{ padding: '18px 0 6px' }}>
        <h2 className="h2" style={{ padding: '0 20px' }}>Saved Searches &amp; New Matches <InfoTip tip="savedSearches" /></h2>
        {isPending && <div className="rc-msg" aria-busy="true">Loading saved searches…</div>}
        {isError && (
          <div className="rc-msg" role="alert">
            <b>Could not load saved searches</b>
            <button className="btn" type="button" onClick={() => void refetch()} style={{ marginTop: 10 }}>Try again</button>
          </div>
        )}
        {data?.items.length === 0 && (
          <div className="rc-msg"><b>No saved searches yet</b>Save a search to track new matches here.</div>
        )}
        {!!data?.items.length && (
        <div className="tw" style={{ marginTop: 6 }}>
          <table className="t">
            <thead>
              <tr>
                <th>Saved Search / Pipeline</th><th>Criteria</th><th className="c">New Matches</th>
                <th className="c">High Match (90%+)</th><th>Last Viewed</th><th>Role Owner</th><th>Action</th>
              </tr>
            </thead>
            <tbody>
              {data?.items.map((search) => (
                <tr key={search.id}>
                  <td className="nm">{search.name}</td>
                  <td>
                    <div className="tags">
                      {search.criteria.map((c) => <span className="tg" key={c}>{c}</span>)}
                    </div>
                  </td>
                  <td className="c">{search.new_matches}</td>
                  <td className="c">{search.high_matches}</td>
                  <td>{search.last_viewed_on ? formatDate(search.last_viewed_on) : '—'}</td>
                  <td>{search.role_owner}</td>
                  {/* the server owns both the wording and the destination */}
                  <td><Link className="btn btn-sm" to={search.action_url}>{search.action_label}</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      <div className="card ov ov-mt" aria-label="Top matches preview">
        <div className="ov-h"><span className="ov-t">Top Matches Preview</span></div>
        <div className="ov-b">
          {matches?.items.map((m) => <MatchRow key={m.reference_code} match={m} />)}
          {/* counts derive from the data, not the hardcoded "6"/"9" of the mockup */}
          {totalNew > shown && (
            <Link className="showmore" to="/recommended">
              Show {totalNew - shown} more <span aria-hidden="true">▾</span>
            </Link>
          )}
          <Link className="lnk" to="/recommended" style={{ display: 'inline-block', marginTop: 10 }}>
            View All {totalNew} Matches &nbsp;→
          </Link>
        </div>
      </div>
    </section>
  )
}

export function PipelineByRole() {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['role-pipelines'],
    queryFn: () => api.getRolePipelines(),
  })
  const lead = data?.items[0]

  return (
    <section className="sec pair">
      <div className="card" style={{ padding: '18px 0 6px' }}>
        <h2 className="h2" style={{ padding: '0 20px' }}>Hiring Pipeline by Role <InfoTip tip="pipelines" /></h2>
        {isPending && <div className="rc-msg" aria-busy="true">Loading pipelines…</div>}
        {isError && (
          <div className="rc-msg" role="alert">
            <b>Could not load pipelines</b>
            <button className="btn" type="button" onClick={() => void refetch()} style={{ marginTop: 10 }}>Try again</button>
          </div>
        )}
        {data?.items.length === 0 && (
          <div className="rc-msg"><b>No open pipelines</b>Create a role pipeline to start tracking candidates.</div>
        )}
        {!!data?.items.length && (
        <div className="tw" style={{ marginTop: 6 }}>
          <table className="t">
            <thead>
              <tr>
                <th>Pipeline</th><th>Role Owner</th><th className="c">New Matches</th><th className="c">Shortlisted</th>
                <th className="c">Conversations</th><th className="c">Interviews</th><th className="c">Decision Docs</th><th>Next Step</th>
              </tr>
            </thead>
            <tbody>
              {data?.items.map((p) => (
                <tr key={p.id}>
                  <td className="nm">{p.name}</td>
                  <td>{p.role_owner}</td>
                  <td className="c">{p.counts.new_matches}</td>
                  <td className="c">{p.counts.shortlisted}</td>
                  <td className="c">{p.counts.conversations}</td>
                  <td className="c">{p.counts.interviews}</td>
                  <td className="c">{p.counts.decision_docs}</td>
                  <td><Link className="btn btn-sm" to={p.next_step_url}>{p.next_step_label}</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      <div className="card ov ov-pp" aria-label="Pipeline preview">
        <div className="ov-h" style={{ paddingBottom: 2 }}>
          <span className="ov-t" style={{ fontWeight: 600, color: 'var(--ink-2)', fontSize: 11 }}>
            Top Matches Preview
          </span>
        </div>
        <div className="ov-b">
          {lead && (
            <>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{lead.name}</div>
              <div className="pp">
                <div><div className="pp-k">New</div><div className="pp-v tnum">{lead.counts.new_matches}</div></div>
                <div><div className="pp-k">Short</div><div className="pp-v tnum">{lead.counts.shortlisted}</div></div>
                <div><div className="pp-k">Conv</div><div className="pp-v tnum">{lead.counts.conversations}</div></div>
                <div><div className="pp-k">Intv</div><div className="pp-v tnum">{lead.counts.interviews}</div></div>
              </div>
              <div className="rows">
                <div className="rw"><span>Decision Docs</span><span>{lead.counts.decision_docs}</span></div>
                <div className="rw"><span>Role Owner</span><span>{lead.role_owner}</span></div>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  )
}
